
import { useState } from 'react';

interface Short {
  id: number;
  title: string;
  description: string;
  duration: string;
  views: string;
  image: string;
  youtubeUrl: string;
}

export default function Shorts() {
  const [hoveredShort, setHoveredShort] = useState<number | null>(null);

  const shorts: Short[] = [
    {
      id: 1,
      title: "Unstoppable Mindset: Conquer Every Challenge & Succeed!",
      description: "Quick mindset shift that changes everything. The power of pivoting when life demands it.",
      duration: "0:58",
      views: "1.5K",
      image: "https://i.ytimg.com/vi/DrF0KT37Uwo/oar2.jpg",
      youtubeUrl: "https://www.youtube.com/@themusclehustlepodcast"
    },
    {
      id: 2,
      title: "Staying Humble: Celebrity Life and Giving Back",
      description: "The secret formula behind every successful hustle. Small actions, massive results.",
      duration: "0:45",
      views: "615",
      image: "https://i.ytimg.com/vi/laJYtqIn-bg/oar2.jpg",
      youtubeUrl: "https://www.youtube.com/@themusclehustlepodcast"
    },
    {
      id: 3,
      title: "From Vision to Victory: Building Your Empire",
      description: "Turn your biggest dreams into reality. The step-by-step blueprint for transforming ideas into success.",
      duration: "0:52",
      views: "743",
      image: "https://i.ytimg.com/vi/CcU2nTw4Q8s/oar2.jpg",
      youtubeUrl: "https://www.youtube.com/@themusclehustlepodcast"
    },
    {
      id: 4,
      title: "Master Your Mindset: The Champion's Approach",
      description: "Develop the mental strength that separates winners from everyone else. Mindset is everything.",
      duration: "0:41",
      views: "1.1K",
      image: "https://i.ytimg.com/vi/j5K3o7vZJkE/oar2.jpg",
      youtubeUrl: "https://www.youtube.com/@themusclehustlepodcast"
    },
    {
      id: 5,
      title: "Love the Work, the Money Will Follow",
      description: "Start your day with the right energy. The morning routine that changes everything.",
      duration: "1:12",
      views: "715",
      image: "https://i.ytimg.com/vi/rDZpaRFUffU/oar2.jpg",
      youtubeUrl: "https://www.youtube.com/@themusclehustlepodcast"
    },
    {
      id: 6,
      title: "It's ok to pivot … He'll redirect you where you belong",
      description: "Transform setbacks into comebacks. Every failure is a lesson in disguise.",
      duration: "0:30",
      views: "17",
      image: "https://i.ytimg.com/vi/Hn5XIhXZDlk/oar2.jpg",
      youtubeUrl: "https://www.youtube.com/@themusclehustlepodcast"
    }
  ];

  const openYouTube = (youtubeUrl: string) => {
    window.open(youtubeUrl, '_blank');
  };

  return (
    <section className="py-16 md:py-20 bg-black">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-red-600 rounded-lg">
              <i className="ri-play-circle-fill text-white text-lg md:text-xl"></i>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">YouTube Shorts</h2>
          </div>
          <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto">
            Quick doses of motivation and wisdom. Bite-sized content that packs a punch for your daily hustle.
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 max-w-7xl mx-auto">
          {shorts.map((short) => (
            <div 
              key={short.id} 
              className="group cursor-pointer"
              onMouseEnter={() => setHoveredShort(short.id)}
              onMouseLeave={() => setHoveredShort(null)}
              onClick={() => openYouTube(short.youtubeUrl)}
            >
              <div className="relative bg-gray-900 rounded-2xl overflow-hidden aspect-[3/4] hover:scale-105 transition-all duration-300">
                <img 
                  src={short.image} 
                  alt={short.title}
                  className="w-full h-full object-cover object-top"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                
                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-red-600/90 rounded-full">
                    <i className="ri-play-fill text-white text-lg md:text-2xl ml-1"></i>
                  </div>
                </div>
                
                {/* Duration */}
                <div className="absolute top-2 md:top-3 right-2 md:right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                  {short.duration}
                </div>
                
                {/* Views */}
                <div className="absolute top-2 md:top-3 left-2 md:left-3 bg-red-600/90 text-white text-xs px-2 py-1 rounded-full font-semibold">
                  {short.views} views
                </div>
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                  <h3 className="text-white font-bold text-sm md:text-base lg:text-lg mb-1 line-clamp-2 leading-tight">
                    {short.title}
                  </h3>
                  <p className="text-gray-300 text-xs md:text-sm line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 leading-tight">
                    {short.description}
                  </p>
                </div>
                
                {/* YouTube Icon */}
                <div className="absolute bottom-2 md:bottom-3 right-2 md:right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center bg-red-600 rounded-full">
                    <i className="ri-youtube-fill text-white text-xs md:text-sm"></i>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-8 md:mt-12">
          <button 
            onClick={() => window.open('https://www.youtube.com/@themusclehustlepodcast', '_blank')}
            className="bg-red-600 hover:bg-red-7
00 text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            Watch More Shorts
          </button>
        </div>
      </div>
    </section>
  );
}
